import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import { AssetColumn, AssetInventoryType, AssetRow, IntegrationInstance } from 'models'
import { CloseDrawerIcon } from 'ui/components/svg'
import { Drawer, SectionWrapper } from './assetDetailsPane.styles'
import AttributeSection from './AttributeSection'
import AttributeSources from './AttributeSources'
import InstanceSection from './InstanceSection'
import SourcePopover from './SourcePopover'
import { useAssetEntity } from '../hooks/useAssetEntity'
import { paneColumnsFor } from '../assetInventory.columns'
import { originUrls } from '../utils/assetSources'

/* Long enough to cross the gap from the logos into the popover without it closing. */
const POPOVER_CLOSE_DELAY_MS = 150

interface Props {
  title: string
  assetType: AssetInventoryType
  columns: AssetColumn[]
  instances: IntegrationInstance[]
  row: AssetRow | null
  open: boolean
  onClose: () => void
}

const AssetDetailsPane: FC<Props> = ({ title, assetType, columns, instances, row, open, onClose }) => {
  const [sourceAnchorEl, setSourceAnchorEl] = useState<HTMLElement | null>(null)
  const { details } = useAssetEntity(assetType, open && row ? row.id : null)

  /* The row's own sources hold the space until the pane's list, which names each value, arrives. */
  const sources = useMemo(() => details?.sources ?? row?.sources ?? [], [details, row])
  const paneColumns = useMemo(() => paneColumnsFor(assetType, columns), [assetType, columns])
  const urls = useMemo(
    () => (details ? originUrls(details.sources, details.members, instances) : {}),
    [details, instances]
  )

  const closeTimer = useRef<ReturnType<typeof setTimeout>>()
  const keepPopoverOpen = useCallback(() => clearTimeout(closeTimer.current), [])
  const closePopover = useCallback(() => {
    clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => setSourceAnchorEl(null), POPOVER_CLOSE_DELAY_MS)
  }, [])

  useEffect(() => () => clearTimeout(closeTimer.current), [])

  return (
    <Drawer anchor="right" open={open && !!row} onClose={onClose}>
      {row && (
        <>
          <Box className="pane-header">
            <Typography className="pane-title">{title}</Typography>
            <IconButton className="close-icon" aria-label="Close details" onClick={onClose}>
              <CloseDrawerIcon />
            </IconButton>
          </Box>

          <Box className="pane-sections styledScrollbar">
            <SectionWrapper>
              <Box className="section-header">
                <Typography className="section-label">Source</Typography>
              </Box>
              <Box
                onMouseEnter={(event) => {
                  keepPopoverOpen()
                  setSourceAnchorEl(event.currentTarget)
                }}
                onMouseLeave={closePopover}
                sx={{ display: 'inline-flex', width: 'fit-content' }}
              >
                <AttributeSources sources={sources} inline />
              </Box>
              <SourcePopover
                anchorEl={sourceAnchorEl}
                sources={sources}
                urls={urls}
                onMouseEnter={keepPopoverOpen}
                onMouseLeave={closePopover}
              />
            </SectionWrapper>

            <InstanceSection sources={sources} />

            {paneColumns.map((column) => (
              <AttributeSection
                /* Keyed by entity so a section left open does not stay open on the next row. */
                key={`${row.id}:${column.name}`}
                column={column}
                row={row}
                attribute={details?.attributes[column.name]}
              />
            ))}
          </Box>
        </>
      )}
    </Drawer>
  )
}

export default AssetDetailsPane
